import Twilio, { Device } from 'twilio-client';

import { setIncomingCall, setCallType, setEndCall } from 'state/call.state';
import { setNotification } from 'state/app.state';
import store from 'state/store';
import { isValidPhoneNumber } from 'utils/functions';

import { Lead, Concierge, CallTypes, AppCopyText } from 'typings';

class TwilioClient {
  private device: Twilio.Device;

  constructor() {
    this.device = new Device();
  }

  initCallDevice(deviceToken: string) {
    if (!this.device.isInitialized) {
      try {
        this.device.setup(deviceToken, {
          debug: true,
          allowIncomingWhileBusy: false,
          closeProtection: false, // maybe use this in the future...
          enableIceRestart: true,
        });

        this.registerTwilioEvents();
        console.log('Twilio Device initialize');
      } catch (error) {
        console.error('Twilio Device initialize error:', error);
      }
    } else if (this.device.token !== deviceToken) {
      this.device.updateToken(deviceToken);
      console.log('Twilio Device update token: Has token:', !!deviceToken);
    }
  }

  registerTwilioEvents() {
    this.device.on('cancel', (conn) => {
      console.log('Twilio Device event: cancel');
    });

    this.device.on('disconnect', (conn) => {
      console.log('Twilio Device event: disconnect');
    });

    this.device.on('error', (error) => {
      console.log('Twilio Device event: error: ', error);
    });

    this.device.on('incoming', (connection: Twilio.Connection) => {
      // accept the incoming connection and start two-way audio
      // device is ready to be answered at this point
      connection.accept();
      // Set a callback to be executed when the connection is accepted
      connection.on('accept', (conn) => {
        store.dispatch(setIncomingCall(false));
        store.dispatch(setCallType(CallTypes.INBOUND));
        console.log('Twilio Device event: connection accept');
      });

      connection.on('error', (error) => {
        console.error('Twilio Device event: connection error:', error);
      });

      console.log('Twilio Device event: incoming');
    });

    // when device connects to another party (inbound or outbound)
    this.device.on('connect', (connection: Twilio.Connection) => {
      if (connection.direction === 'OUTGOING') {
        // we made an outbound call
        store.dispatch(setCallType(CallTypes.OUTBOUND));
      }

      connection.on('disconnect', () => {
        store.dispatch(setEndCall());
        console.error('Twilio Device event: connection disconnect');
      });

      console.log('Twilio Device event: connect, Direction: ', connection.direction);
    });

    this.device.on('offline', () => {
      console.log('Twilio Device event: offline');
    });

    this.device.on('ready', () => {
      console.log('Twilio Device event: ready');
    });
  }

  dialOutbound(lead: Lead, concierge: Concierge) {
    if (!lead?.phone || !isValidPhoneNumber(lead?.phone, lead?.phoneCountryIso || 'US')) {
      store.dispatch(
        setNotification({
          type: 'error',
          text: AppCopyText.INVALID_PHONE,
        })
      );
      return;
    }

    const params: { [key: string]: any } = {
      From: lead?.engagedPhone || lead?.agent?.twilioNumber,
      To: lead?.phone,
      Direction: 'outbound',
      LeadId: lead.id,
      ConciergeId: concierge.id,
    };

    // create an outbound connection
    this.device.connect(params);
    console.log('Twilio Device connect: dialOutbound');
  }

  muteCall(shouldMute: boolean) {
    if (!this.device.token) {
      console.log('Twilio Device token missing: muteCall');
      return;
    }

    this.device.activeConnection()?.mute(shouldMute);
    console.log('Twilio Device active connection mute:', shouldMute);
  }

  endCall() {
    if (!this.device.token) {
      console.log('Twilio Device token missing: endCall');
      return;
    }

    this.device.disconnectAll();
    console.log('Twilio Device disconnect all: endCall');
  }

  /**
   * @param digits - can contain the characters 0-9, *, #, and w.
   */
  sendDigits(digits: string) {
    const activeConnection = this.device.activeConnection();
    activeConnection?.sendDigits(digits);
    console.log('Twilio Device active connection send digits');
  }
}

const twilioClient = new TwilioClient();
export default twilioClient;
